import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  CalendarWidgetEvent,
  GCalEvent,
  GoogleCalendarWidgetProps,
} from "../Contract/GoogleTypes";
import {
  startOfDay,
  getMonthGrid,
  addDays,
  parseGCalEvent,
  isSameDay,
  formatMonthUpper,
  formatTime,
  listCalendars,
  fetchGoogleCalColors,
  GoogleCalColorsResponse,
} from "../services/googleUtil";
import { Context } from "../App";
import { GoogleCalTokenApi } from "../services/googleTokenApi";
import { PanelHeader, PanelState } from "./Panel";

export default function GoogleCalendarWidgetIphone({
  clientId,
  apiKey,
  calendarId = "primary",
  scope = "https://www.googleapis.com/auth/calendar.readonly",
  timeZone = "Europe/London",
  maxResults = 50,
  onAddEventClick,
  showAllCalendars = true,
}: GoogleCalendarWidgetProps) {
  const { calendarToken: token } = useContext(Context);

  const [events, setEvents] = useState<CalendarWidgetEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [colors, setColors] = useState<GoogleCalColorsResponse | null>(null);
  const [calendarMetaById, setCalendarMetaById] = useState<Record<string, any>>(
    {},
  );

  const today = useMemo(() => startOfDay(new Date()), []);
  const [selectedDay, setSelectedDay] = useState<Date>(today);
  const [monthAnchor, setMonthAnchor] = useState<Date>(() =>
    startOfDay(new Date()),
  ); // iPhone widget shows current month

  const isTokenValid = useMemo(() => Boolean(token), [token]);

  const isPublicMode = Boolean(apiKey) && calendarId !== "primary";

  const fetchForMonth = async () => {
    setLoading(true);
    setError(null);

    const { first, days } = getMonthGrid(monthAnchor);
    const monthStart = startOfDay(first);
    const monthEnd = addDays(startOfDay(days[days.length - 1].date), 1);

    const baseParams = new URLSearchParams({
      timeMin: monthStart.toISOString(),
      timeMax: monthEnd.toISOString(),
      singleEvents: "true",
      orderBy: "startTime",
      maxResults: String(maxResults),
      timeZone,
    });

    try {
      // PUBLIC MODE can only read ONE public calendar by id
      if (isPublicMode) {
        if (!apiKey)
          throw new Error("Missing apiKey for public calendar mode.");

        const params = new URLSearchParams(baseParams);
        params.set("key", apiKey);

        const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(
          calendarId,
        )}/events?${params.toString()}`;

        const res = await fetch(url);
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`Calendar API error (${res.status}): ${text}`);
        }

        const data: { items?: GCalEvent[] } = await res.json();
        setEvents(
          (data.items ?? [])
            .map((item) => parseGCalEvent(item))
            .filter(Boolean) as CalendarWidgetEvent[],
        );
        return;
      }

      // OAuth required from here on - get a valid token (auto-refreshes if needed)
      const tokenApi = new GoogleCalTokenApi();
      const validToken = await tokenApi.getValidAccessToken();

      if (!validToken) throw new Error("Not authenticated or token expired.");

      const [calendarList, colorsRes] = await Promise.all([
        listCalendars(validToken),
        fetchGoogleCalColors(validToken),
      ]);

      // Cache for rendering (optional, but handy)
      setColors(colorsRes);

      const metaMap: Record<string, any> = {};
      for (const c of calendarList) metaMap[c.id] = c;
      setCalendarMetaById(metaMap);

      const calendarIds = showAllCalendars
        ? calendarList
            // "selected" is what Google shows by default in the UI; you can remove this filter if you want everything
            .filter((c) => c.selected !== false)
            .map((c) => c.id)
        : [calendarId];

      // Fetch events for each calendar in parallel
      const all = await Promise.all(
        calendarIds.map(async (cid: string) => {
          const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(
            cid,
          )}/events?${baseParams.toString()}`;

          const res = await fetch(url, {
            headers: { Authorization: `Bearer ${validToken}` },
          });

          if (!res.ok) {
            // If one calendar fails (e.g. permissions), skip it rather than breaking the whole widget
            console.warn("Calendar fetch failed:", cid, res.status);
            return [] as CalendarWidgetEvent[];
          }

          const data: { items?: GCalEvent[] } = await res.json();
          return (
            ((data.items ?? [])
              .map((item) => parseGCalEvent(item, cid))
              .filter(Boolean) as CalendarWidgetEvent[]) ?? []
          );
        }),
      );

      const merged = all
        .flat()
        .sort((a, b) => a.start.getTime() - b.start.getTime());

      const withColors = merged.map((ev: CalendarWidgetEvent) => {
        const cal = ev.sourceCalendarId
          ? metaMap[ev.sourceCalendarId]
          : undefined;

        // 1) Per-event color
        const eventPalette = ev.eventColorId
          ? colorsRes.event?.[ev.eventColorId]
          : undefined;
        if (eventPalette?.background || eventPalette?.foreground) {
          return {
            ...ev,
            colorBg: eventPalette.background,
            colorFg: eventPalette.foreground,
          };
        }

        // 2) Calendar explicit colors
        if (cal?.backgroundColor || cal?.foregroundColor) {
          return {
            ...ev,
            colorBg: cal.backgroundColor,
            colorFg: cal.foregroundColor,
          };
        }

        // 3) Calendar colorId palette
        const calPalette = cal?.colorId
          ? colorsRes.calendar?.[cal.colorId]
          : undefined;
        if (calPalette?.background || calPalette?.foreground) {
          return {
            ...ev,
            colorBg: calPalette.background,
            colorFg: calPalette.foreground,
          };
        }

        return ev;
      });

      setEvents(withColors);
    } catch (e) {
      setEvents([]);
      setError(e instanceof Error ? e.message : "Failed to fetch events.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Poll for updates once an hour and snap back to today's date/month
    if (!isPublicMode && !isTokenValid) return;

    const tick = () => {
      const now = startOfDay(new Date());
      setSelectedDay(now);
      setMonthAnchor(now);
      void fetchForMonth();
    };

    const id = window.setInterval(tick, 60 * 60 * 1000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPublicMode,
    isTokenValid,
    calendarId,
    maxResults,
    timeZone,
    showAllCalendars,
  ]);

  useEffect(() => {
    if (isPublicMode) {
      void fetchForMonth();
      return;
    }
    if (calendarId === "primary" && !isTokenValid) return;
    void fetchForMonth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPublicMode, isTokenValid, calendarId, maxResults, timeZone]);

  const { days: gridDays } = useMemo(
    () => getMonthGrid(monthAnchor),
    [monthAnchor],
  );

  const eventsForSelectedDay = useMemo(() => {
    const dayStart = startOfDay(selectedDay);
    const nextDay = addDays(dayStart, 1);

    return events
      .filter((ev) => {
        if (ev.allDay) return isSameDay(ev.start, dayStart);
        return (
          ev.start < nextDay &&
          (ev.end ? ev.end > dayStart : ev.start >= dayStart)
        );
      })
      .sort((a, b) => a.start.getTime() - b.start.getTime());
  }, [events, selectedDay]);

  const hasAnyEventOnDay = useMemo(() => {
    const set = new Set<string>();
    for (const ev of events) {
      set.add(startOfDay(ev.start).toISOString());
    }
    return (d: Date) => set.has(startOfDay(d).toISOString());
  }, [events]);

  const canShow = isPublicMode || isTokenValid;
  const isToday = isSameDay(selectedDay, today);
  const selectedLabel = isToday
    ? "Today"
    : selectedDay.toLocaleDateString("en-GB", {
        weekday: "short",
        day: "numeric",
        month: "short",
      });

  return (
    <div className="gcw2">
      <PanelHeader
        label={`Calendar / ${formatMonthUpper(monthAnchor)}`}
        meta={
          canShow && !loading
            ? `${eventsForSelectedDay.length} ${
                eventsForSelectedDay.length === 1 ? "event" : "events"
              }`
            : undefined
        }
        accent={canShow && eventsForSelectedDay.length > 0}
      />

      <div className="gcw2__inner">
        {/* Left: month grid */}
        <div className="gcw2__left">
          <div className="gcw2__dow">
            {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
              <div key={`${d}-${i}`} className="gcw2__dowCell">
                {d}
              </div>
            ))}
          </div>

          <div className="gcw2__grid">
            {gridDays.map(({ date, inMonth }) => {
              const selected = isSameDay(date, selectedDay);
              const hasEvent = hasAnyEventOnDay(date);

              return (
                <button
                  key={date.toISOString()}
                  type="button"
                  className={[
                    "gcw2__day",
                    !inMonth ? "is-muted" : "",
                    selected ? "is-selected" : "",
                    isSameDay(date, today) ? "is-today" : "",
                    hasEvent ? "has-event" : "",
                  ].join(" ")}
                  onClick={() => setSelectedDay(startOfDay(date))}
                  title={date.toDateString()}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
        </div>

        {/* Right: agenda for the selected day */}
        <div className="gcw2__right">
          <div className="gcw2__dayhead">
            <span>{selectedLabel}</span>
            {!isToday && (
              <button
                type="button"
                className="gcw2__back"
                onClick={() => setSelectedDay(today)}
              >
                Today →
              </button>
            )}
          </div>

          {error && <PanelState tone="error">{error}</PanelState>}

          {!error && loading && <PanelState>Loading…</PanelState>}

          {!error && !loading && !canShow && (
            <PanelState>Connect Google in Admin</PanelState>
          )}

          {!error && !loading && canShow && eventsForSelectedDay.length === 0 && (
            <PanelState>Nothing scheduled</PanelState>
          )}

          {!error && !loading && canShow && eventsForSelectedDay.length > 0 && (
            <div className="gcw2__events">
              {eventsForSelectedDay.map((ev) => (
                <div key={ev.id} className="gcw2__event">
                  <span className="gcw2__eventTime">
                    {ev.allDay ? "All day" : formatTime(ev.start)}
                  </span>
                  <span
                    className="gcw2__eventSwatch"
                    style={ev.colorBg ? { background: ev.colorBg } : undefined}
                  />
                  <span className="gcw2__eventTitle">{ev.title}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
